
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Trash2, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function StorageCleanup() {
  const [isClearing, setIsClearing] = useState(false);
  const { toast } = useToast();

  const clearStorage = async () => {
    setIsClearing(true);
    try {
      // List all files in the templates bucket
      const { data: templateFiles, error: listError } = await supabase.storage
        .from('templates')
        .list();

      if (listError) {
        console.error('Error listing template files:', listError);
        throw listError;
      }

      if (templateFiles && templateFiles.length > 0) {
        // Extract file paths
        const filePaths = templateFiles.map(file => file.name);
        
        // Delete all files
        const { error: deleteError } = await supabase.storage
          .from('templates')
          .remove(filePaths);

        if (deleteError) {
          console.error('Error deleting template files:', deleteError);
          throw deleteError;
        }

        toast({
          title: "Storage Cleared",
          description: `Successfully deleted ${filePaths.length} files from storage`,
        });
      } else {
        toast({
          title: "Storage Already Clean",
          description: "No files found in storage to delete",
        });
      }

    } catch (error) {
      console.error('Error clearing storage:', error);
      toast({
        title: "Error",
        description: "Failed to clear storage files",
        variant: "destructive",
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          Storage Cleanup
        </CardTitle>
        <CardDescription>
          Clear all files from Supabase storage buckets. This action cannot be undone.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="destructive" 
              disabled={isClearing}
              className="flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {isClearing ? "Clearing Storage..." : "Clear All Storage Files"}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action will permanently delete all files from the storage buckets.
                This includes all template files and cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={clearStorage}
                className="bg-red-600 hover:bg-red-700"
              >
                Yes, clear all files
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
